import React, { useState } from 'react';
interface OrgItemProps {
  title: string;
  description: string;
  color: string;
  isExpanded?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}
function OrgItem({
  title,
  description,
  color,
  isExpanded = false,
  onClick,
  children
}: OrgItemProps) {
  return <div className={`bg-white rounded-lg shadow-soft overflow-hidden transition-all duration-300 hover:shadow-hover animate-fadeIn ${isExpanded ? 'ring-2' : ''}`} style={{
    borderColor: color,
    ...(isExpanded && {
      ringColor: color
    })
  }} data-id="element-243">
      <div className="h-2" style={{
      backgroundColor: color
    }} data-id="element-244"></div>
      <div className="p-5" data-id="element-245">
        <button onClick={onClick} className="w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-md" style={{
        ...(isExpanded && {
          color
        })
      }} data-id="element-246">
          <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-247">{title}</h3>
          <p className="text-charcoal/80 text-sm" data-id="element-248">{description}</p>
        </button>
        {children && <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[1000px] mt-4' : 'max-h-0'}`} data-id="element-249">
            {children}
          </div>}
      </div>
    </div>;
}
interface RoleItemProps {
  title: string;
  description: string;
  index: number;
}
function RoleItem({
  title,
  description,
  index
}: RoleItemProps) {
  return <div className="flex items-start gap-4 py-3 animate-fadeIn" style={{
    animationDelay: `${index * 50}ms`
  }} data-id="element-250">
      <div className="bg-sfrr-pink/10 rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-251">
        <span className="text-sfrr-pink text-sm font-medium" data-id="element-252">{index + 1}</span>
      </div>
      <div data-id="element-253">
        <h4 className="font-medium text-charcoal" data-id="element-254">{title}</h4>
        <p className="text-charcoal/70 text-sm" data-id="element-255">{description}</p>
      </div>
    </div>;
}
export function OrganogramSection() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  const boardOfTrustees = [{
    title: 'Chairperson',
    description: "Provides overall governance leadership and ensures alignment with the Foundation's mission."
  }, {
    title: 'Vice Chairperson',
    description: 'Supports the Chairperson and acts in their stead when necessary.'
  }, {
    title: 'Founder & President',
    description: "Custodian of the Foundation's vision, legacy, and long-term direction."
  }, {
    title: 'Trustee (Finance & Investments)',
    description: 'Offers oversight on financial sustainability and long-term resource management.'
  }, {
    title: 'Trustee (Community & Humanitarian Affairs)',
    description: 'Provides guidance on community engagement, outreach, and social impact.'
  }];
  const executiveCommittee = [{
    title: 'Executive Director / CEO',
    description: 'Oversees daily management, execution of strategy, and coordination across departments.'
  }, {
    title: 'Director of Programs & Projects',
    description: 'Designs and manages all development and humanitarian programs.'
  }, {
    title: 'Director of Finance & Administration (CFO)',
    description: 'Manages financial systems, budgets, and compliance.'
  }, {
    title: 'Director of Partnerships & Resource Mobilization',
    description: 'Leads fundraising, donor relations, and external collaborations.'
  }, {
    title: 'Director of Communications & Public Affairs',
    description: 'Manages branding, public relations, media, and digital visibility.'
  }, {
    title: 'Director of Monitoring, Evaluation & Learning (MEL)',
    description: 'Tracks performance and ensures accountability.'
  }, {
    title: 'Director of Legal & Compliance',
    description: 'Provides legal guidance and ensures regulatory compliance.'
  }];
  const essentialStaff = [{
    title: 'Program Managers',
    description: 'Lead specific thematic focus areas (Healthcare, Agriculture, Education, Water, Humanitarian Services).'
  }, {
    title: 'Finance & Accounts Officers',
    description: 'Handle day-to-day accounting and reporting.'
  }, {
    title: 'HR & Administration Manager',
    description: 'Manages staff recruitment, welfare, and office administration.'
  }, {
    title: 'Monitoring & Evaluation Officers',
    description: 'Collect data and assess impact of programs.'
  }, {
    title: 'Communications & Media Officers',
    description: 'Oversee content creation, press, and digital engagement.'
  }, {
    title: 'Partnership & Fundraising Officers',
    description: 'Support donor engagement and proposal development.'
  }, {
    title: 'Project Field Coordinators',
    description: 'Supervise community-based project implementation.'
  }, {
    title: 'Volunteers & Interns',
    description: 'Provide flexible support for outreach and program delivery.'
  }];
  return <section id="organogram" className="py-16 bg-gradient-to-b from-white to-warm-ivory scroll-mt-20" data-id="element-256">
      <div className="container mx-auto px-4" data-id="element-257">
        <div className="text-center mb-12" data-id="element-258">
          <div className="inline-block px-4 py-1 mb-6 rounded-full bg-sfrr-pink/20 text-sfrr-pink text-sm font-medium animate-fadeIn" data-id="element-259">
            ORGANIZATIONAL STRUCTURE
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4 animate-fadeIn animation-delay-100" data-id="element-260">
            Our Leadership Structure
          </h2>
          <p className="max-w-2xl mx-auto text-charcoal/80 animate-fadeIn animation-delay-200" data-id="element-261">
            The Senator Fatima Raji Rasaki Foundation operates through a
            structured leadership approach that ensures effective governance,
            management, and implementation of our mission.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6" data-id="element-262">
          {/* Board of Trustees */}
          <OrgItem title="Board of Trustees" description="Provides strategic direction and governance oversight for the foundation" color="#8dc63f" isExpanded={expandedSection === 'board'} onClick={() => toggleSection('board')} data-id="element-263">
            <div className="border-t border-gray-100 pt-3" data-id="element-264">
              {boardOfTrustees.map((role, index) => <RoleItem key={index} title={role.title} description={role.description} index={index} data-id="element-265" />)}
            </div>
          </OrgItem>
          {/* Executive Working Committee */}
          <OrgItem title="Executive Working Committee" description="Responsible for operational leadership and program implementation" color="#e6007e" isExpanded={expandedSection === 'executive'} onClick={() => toggleSection('executive')} data-id="element-266">
            <div className="border-t border-gray-100 pt-3" data-id="element-267">
              {executiveCommittee.map((role, index) => <RoleItem key={index} title={role.title} description={role.description} index={index} data-id="element-268" />)}
            </div>
          </OrgItem>
          {/* Essential Staff */}
          <OrgItem title="Essential Staff" description="The dedicated team that executes our programs and daily operations" color="#00aeef" isExpanded={expandedSection === 'staff'} onClick={() => toggleSection('staff')} data-id="element-269">
            <div className="border-t border-gray-100 pt-3" data-id="element-270">
              {essentialStaff.map((role, index) => <RoleItem key={index} title={role.title} description={role.description} index={index} data-id="element-271" />)}
            </div>
          </OrgItem>
        </div>
        <div className="mt-12 text-center" data-id="element-272">
          <p className="text-charcoal/80 italic animate-fadeIn animation-delay-300" data-id="element-273">
            "Our organizational structure is designed to ensure accountability,
            efficiency, and maximum impact in all our initiatives."
          </p>
        </div>
      </div>
    </section>;
}